import { Spacer } from '@kizen/kds/Spacer';
import { Typography } from '@kizen/kds/Typography';
import { Dots } from './styles';
import { ResolvedPlanDetails } from '../hooks/useSelectedPlanResolution';
import { formatBillingCurrency } from '../../util';
import {
  getAnnualPrice,
  getDiscountedMonthlyPrice,
  getAnnualTotalFromDiscountedMonthlies,
} from '../util';

export type BillingCadence = 'month' | 'year';

const priceForCadence = (
  monthlyAmount: number,
  cadence: BillingCadence,
  annualDiscountPercentage: number
) => {
  if (cadence === 'year') {
    return getAnnualPrice(monthlyAmount, annualDiscountPercentage);
  }
  return monthlyAmount;
};

export const computeCadenceAmounts = (
  cadence: BillingCadence,
  coreMonthly: number,
  coreDiscountPct: number,
  automationMonthly: number,
  automationDiscountPct: number,
  marketingMonthly: number,
  marketingDiscountPct: number
) => {
  const coreLine = priceForCadence(coreMonthly, cadence, coreDiscountPct);
  const automationLine = priceForCadence(
    automationMonthly,
    cadence,
    automationDiscountPct
  );
  const marketingLine = priceForCadence(
    marketingMonthly,
    cadence,
    marketingDiscountPct
  );

  let total: number;
  if (cadence === 'year') {
    total = getAnnualTotalFromDiscountedMonthlies([
      getDiscountedMonthlyPrice(coreMonthly, coreDiscountPct),
      getDiscountedMonthlyPrice(automationMonthly, automationDiscountPct),
      getDiscountedMonthlyPrice(marketingMonthly, marketingDiscountPct),
    ]);
  } else {
    total =
      Math.round((coreMonthly + automationMonthly + marketingMonthly) * 100) /
      100;
  }

  return { coreLine, automationLine, marketingLine, total };
};

export const planDiscount = (plan: ResolvedPlanDetails) =>
  plan?.annualDiscountPercentage ?? 0;

type SummaryLineProps = {
  label: string;
  detail: string;
  amount: number;
  symbol?: string;
  isAdditional?: boolean;
};

export const SummaryLine = (props: SummaryLineProps) => {
  const { label, detail, amount, symbol, isAdditional } = props;

  return (
    <div>
      <Typography size="lg">
        {isAdditional ? '+ ' : ''}
        {label}
      </Typography>
      <Spacer size={15} />
      <div className="pl-4 flex items-end">
        <Typography className="pr-1">{detail}</Typography>
        <Dots className="grow" />
        <Typography className="pl-1">
          {formatBillingCurrency(amount, false, symbol)}
        </Typography>
      </div>
    </div>
  );
};
